import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { fetchCategories, fetchRepresentatives } from "../../utils/Utils";
import {
  CategoriesType,
  RepresentativeType,
  StudentFormType,
} from "../../types/entitiesModel";

interface StudentFormNoSendType {
  formState: StudentFormType;
  setFormStateStudent: (value: StudentFormType) => void;
  handleChangeForm: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  repExists: boolean;
  setRepExists: (value: boolean) => void;
  showRepresentative: boolean;
}

const StudentFormNoSend: React.FC<StudentFormNoSendType> = (
  props: StudentFormNoSendType
) => {
  const [categories, setCategories] = React.useState<CategoriesType[]>([]);
  const [representatives, setRepresentatives] = React.useState<
    RepresentativeType[]
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Manejo de la carga de la imagen
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Por favor, suba un archivo de imagen válido.");
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        setError("El tamaño del archivo no debe exceder 2 MB.");
        return;
      }

      setError(null);

      const reader = new FileReader();
      reader.onloadend = () => {
        props.setFormStateStudent({
          ...props.formState,
          imageString: reader.result as string, // Asignamos la imagen a la forma
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Cargar categorías y representantes al montar el componente
  React.useEffect(() => {
    fetchCategories(setLoading, setCategories, setError);

    if (props.showRepresentative) {
      fetchRepresentatives(setLoading, setRepresentatives, setError);
    }
  }, [props.showRepresentative]);

  // Limpiar el ID del representante si no existe
  React.useEffect(() => {
    if (!props.repExists && props.formState.representative_id !== 0) {
      props.setFormStateStudent({ ...props.formState, representative_id: 0 });
    }
  }, [props.repExists, props.formState.representative_id, props]);

  return (
    <React.Fragment>
      {!loading &&
      categories.length > 0 &&
      (!props.showRepresentative || representatives.length > 0) ? (
        <>
          {error && (
            <Alert
              sx={{
                mx: "auto",
                my: 2,
                width: "95%",
              }}
              severity="error"
            >
              {error}
            </Alert>
          )}
          <Grid container spacing={0}>
            <Grid item lg={12} md={12} xs={12}>
              <Card variant="outlined" sx={{ p: 0 }}>
                <Box
                  sx={{ padding: "15px 30px" }}
                  display="flex"
                  alignItems="center"
                >
                  <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                    Información del estudiante
                  </Typography>
                </Box>
                <Divider />
                <CardContent sx={{ padding: "30px" }}>
                  <form>
                    <Grid container spacing={2}>
                      <Grid item md={6} xs={12}>
                        <TextField
                          id="name"
                          label="Nombre"
                          variant="outlined"
                          placeholder="Nombre"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="name"
                          onChange={props.handleChangeForm}
                        />
                        <TextField
                          id="document"
                          label="Documento"
                          type="text"
                          variant="outlined"
                          placeholder="Documento"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="document"
                          onChange={props.handleChangeForm}
                        />
                        {props.showRepresentative && (
                          <Autocomplete
                            disabled={!props.repExists}
                            options={representatives}
                            getOptionLabel={(option) => option.name}
                            onChange={(event, newValue) => {
                              props.setFormStateStudent({
                                ...props.formState,
                                representative_id: newValue ? newValue.id : 0,
                              });
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Representante"
                                variant="outlined"
                                fullWidth
                                placeholder="Selecciona un representante"
                              />
                            )}
                            sx={{
                              mb: 2,
                              "& .Mui-disabled": {
                                color: "rgba(0, 0, 0, 0.6)",
                                backgroundColor: "#f7f7f7",
                              },
                            }}
                            value={
                              representatives.find(
                                (rep) =>
                                  rep.id === props.formState.representative_id
                              ) || null
                            }
                          />
                        )}
                        <TextField
                          id="phone"
                          label="Teléfono"
                          type="text"
                          variant="outlined"
                          placeholder="Teléfono"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="phone"
                          onChange={props.handleChangeForm}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <TextField
                          id="email"
                          label="Email"
                          type="text"
                          variant="outlined"
                          placeholder="Email"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="email"
                          onChange={props.handleChangeForm}
                        />
                        <TextField
                          id="address"
                          label="Dirección"
                          type="text"
                          variant="outlined"
                          placeholder="Dirección"
                          fullWidth
                          sx={{ mb: 2 }}
                          name="address"
                          onChange={props.handleChangeForm}
                        />
                        <TextField
                          fullWidth
                          id="category_id"
                          variant="outlined"
                          select
                          label="Categoría"
                          name="category_id"
                          value={props.formState.category_id}
                          onChange={props.handleChangeForm}
                          sx={{ mb: 2 }}
                        >
                          <MenuItem value={0}>
                            Selecciona una categoría
                          </MenuItem>
                          {categories.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Box
                        sx={{
                          mt: 2,
                          width: '100%',
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        {/* Vista previa de la imagen */}
                        {props.formState.imageString && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <img
                              src={props.formState.imageString}
                              alt="Vista previa"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          </Box>
                        )}

                        {/* Botón de subir imagen */}
                        <Button
                          variant="contained"
                          component="label"
                          sx={{ width: "fit-content" }} // Botón ajustado al contenido
                        >
                          Subir una imagen
                          <input
                            type="file"
                            hidden
                            onChange={handleImageUpload}
                          />
                        </Button>
                      </Box>
                    </Grid>
                  </form>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      ) : (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      )}
    </React.Fragment>
  );
};

export default StudentFormNoSend;
